import { toRevenueShareClass } from '@upright/common/model/input/blanket/blanket-product';
import { zipOrThrow } from '@upright/common/utils/lodash-extensions';
import { safeAssert as assert } from '@upright/common/safe-assert';
import { isNil, sum, sumBy } from 'lodash';
import { computeRevenueSharesPerRatio, getRevenueShareRatio } from '.';
import { WithChildren } from '../../blanket/blanket-product/hierarchy-utils';
import { BlanketProduct } from '../../blanket/blanket-product/types';
import { CompanyProductSetProduct } from './types';

export interface ResolvedWeight {
  resolvedWeight: number;
}

export function resolveProductWeightsRecursive<T extends Pick<BlanketProduct, 'weightType' | 'weight'>>(
  products: Array<WithChildren<T>>,
  totalWeight = 1,
): Array<WithChildren<T & ResolvedWeight>> {
  if (!products.length) {
    return [];
  }

  const topLevelProductWeights = resolveRelativeProductWeights(products, totalWeight);
  const productsWithWeights = zipOrThrow(products, topLevelProductWeights).map(([product, weight]) => ({
    ...product,
    resolvedWeight: weight,
    children: resolveProductWeightsRecursive(product.children, weight),
  }));
  return productsWithWeights;
}

function resolveRelativeProductWeights(
  products: Array<Pick<BlanketProduct, 'weightType' | 'weight'>>,
  totalWeight: number,
) {
  if (!products.length) {
    return [];
  }
  const productRevenueShares = products.map(product => toRevenueShareInfo(product));
  const productsWithoutRevenueShare = productRevenueShares.filter(product => isNil(product.revenueShare));

  const assignedShare = sumBy(productRevenueShares, p => p.revenueShare || 0);
  const unassignedShare = Math.max(totalWeight - assignedShare, 0);

  const revenueSharePerRatio = computeRevenueSharesPerRatio(productsWithoutRevenueShare, unassignedShare);
  const productWeightsUnscaled = productRevenueShares.map(revenueShareInfo =>
    resolveProductWeight(revenueShareInfo, revenueSharePerRatio),
  );

  const productWeights = scaleToTotal(productWeightsUnscaled, totalWeight);
  return productWeights;
}

type RevenueShareInfo = Pick<CompanyProductSetProduct, 'revenueShare' | 'revenueShareClass'>;

function toRevenueShareInfo(product: Pick<BlanketProduct, 'weightType' | 'weight'>): RevenueShareInfo {
  const { weight, weightType } = product;
  const hasExactWeight = weightType === 'relative';
  return {
    revenueShare: hasExactWeight && !isNil(weight) ? Math.max(weight, 0) : undefined,
    revenueShareClass: !hasExactWeight ? toRevenueShareClass(weightType) : undefined,
  };
}

function resolveProductWeight(revenueShareInfo: RevenueShareInfo, revenueSharePerRatio: number) {
  const { revenueShare, revenueShareClass } = revenueShareInfo;
  if (!isNil(revenueShare)) {
    return revenueShare;
  }
  return getRevenueShareRatio(revenueShareClass) * revenueSharePerRatio;
}

function scaleToTotal(values: number[], totalValue: number) {
  assert(totalValue >= 0);
  if (totalValue === 0 || values.every(v => v === 0)) {
    return values.map(() => 0);
  }
  const weightSum = sum(values);
  assert(weightSum > 0);

  const scalar = totalValue / weightSum;
  const productWeights = values.map(weight => weight * scalar);
  return productWeights;
}
